import { useCallback, useEffect, useState } from 'react';
import type { LobbyInfo } from '../../api/types';
import {
  TRACKED_SERIES_KEY,
  forgetSeries,
  markSeen,
  parseTrackedSeries,
  pruneTrackedSeries,
  rememberSeries,
  type TrackedSeries,
} from './tracker';

function readStorage(): TrackedSeries[] {
  try {
    const raw = window.localStorage.getItem(TRACKED_SERIES_KEY);
    return pruneTrackedSeries(parseTrackedSeries(raw), Date.now());
  } catch {
    return [];
  }
}

function writeStorage(list: TrackedSeries[]): void {
  try {
    window.localStorage.setItem(TRACKED_SERIES_KEY, JSON.stringify(list));
  } catch {
    // Private browsing or a full quota: the in-memory list still serves this session.
  }
}

/**
 * The series this browser started, kept in localStorage and refreshed against
 * the live lobby list so the page can tell "game 2 of a bo3" from a plain lobby.
 */
export function useTrackedSeries(lobbies: LobbyInfo[] | undefined) {
  const [tracked, setTracked] = useState<TrackedSeries[]>(readStorage);

  const update = useCallback((updater: (current: TrackedSeries[]) => TrackedSeries[]) => {
    setTracked((current) => {
      const next = updater(current);
      if (next !== current) writeStorage(next);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!lobbies) return;
    update((current) => markSeen(current, lobbies, Date.now()));
  }, [lobbies, update]);

  const remember = useCallback(
    (entry: TrackedSeries) => update((current) => rememberSeries(current, entry)),
    [update],
  );
  const forget = useCallback(
    (botId: string) => update((current) => forgetSeries(current, botId)),
    [update],
  );

  return { tracked, remember, forget };
}
